import { useCallback, useState } from 'react';

import { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

import type { REACH_OPTIONS } from '../../constants';

import { useFormSchema } from './useFormSchema';

type ReachOption = (typeof REACH_OPTIONS)[number]['name'];
type FormData = { locationId: string; locationName: string; reach: ReachOption; location?: string };
type FormErrors = { reach: string | null; location: string | null };

type UseLocationReturn = ModelHookResult<
	{ selectedReach: ReachOption | undefined; formErrors: FormErrors },
	{
		back: () => void;
		submit: (formData: FormData) => void;
		handleReachChange: (value: ReachOption | undefined) => void;
		handleLocationChange: () => void;
	}
>;

export function useLocation(): UseLocationReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const initialReach = data.draft?.petitionDraft?.reach ?? undefined;
	const [selectedReach, setSelectedReach] = useState<ReachOption | undefined>(initialReach);

	const [formErrors, setFormErrors] = useState<FormErrors>({ reach: null, location: null });
	const resetFieldError = useCallback(
		(field: keyof FormErrors) => {
			setFormErrors((prev) => ({ ...prev, [field]: null }));
		},
		[setFormErrors],
	);

	const handleReachChange = useCallback(
		(value: ReachOption | undefined) => {
			setSelectedReach(value);
			resetFieldError('reach');
		},
		[resetFieldError],
	);

	const handleLocationChange = useCallback(() => {
		resetFieldError('location');
	}, [resetFieldError]);

	const schema = useFormSchema();
	const submit = useCallback(
		(formData: FormData) => {
			const result = schema.safeParse(formData);

			if (!result.success) {
				const { fieldErrors } = z.flattenError(result.error);
				const reachError = fieldErrors.reach?.[0] ?? null;
				const locationIdError = fieldErrors.locationId?.[0] ?? null;
				const locationNameError = fieldErrors.locationName?.[0] ?? null;
				const locationError = locationIdError || locationNameError;
				setFormErrors({ reach: reachError, location: locationError });
				return;
			}

			const { reach, locationId, locationName } = result.data;
			void track('sap_location_submit', { reach, location: locationName || undefined });

			const petitionDraft =
				reach !== 'local' ? { reach, location: null } : { reach, location: { id: locationId, name: locationName } };

			actions.send({
				type: 'submit',
				draft: { petitionDraft },
			});
		},
		[actions, schema, track],
	);

	const back = useCallback(() => {
		void track('sap_location_back');
		actions.send({ type: 'back' });
	}, [actions, track]);

	return {
		data: { selectedReach, formErrors },
		actions: { handleReachChange, handleLocationChange, back, submit },
	};
}
